import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Sparkle, X, Send, Loader2, Wand2, Palette, HelpCircle } from "lucide-react";

const STORAGE_KEY = "geneo_nova_session_v1";
const MAX_LEN = 1200;

const QUICK_CMDS = [
  { cmd: "/prompt ", label: "Rewrite prompt", icon: Wand2 },
  { cmd: "/style ", label: "Style suggest", icon: Palette },
  { cmd: "/help", label: "How to use Nova", icon: HelpCircle },
];

const WELCOME = {
  id: "welcome",
  role: "assistant",
  text: "I'm Nova — your Geneo copilot. Ask me to rewrite prompts, suggest styles, or walk you through any Studio mode. Try `/prompt a Nubian priestess in cosmic armor` or `/style cyberpunk pharaoh`.",
};

const mkMsgId = () => `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export default function NovaChat() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Load prior history when session already exists
  useEffect(() => {
    if (!sessionId || !open) return;
    api.get(`/agent/sessions/${sessionId}`).then((r) => {
      if (Array.isArray(r.data?.messages) && r.data.messages.length > 0) {
        setMessages([WELCOME, ...r.data.messages.map((m) => ({ id: mkMsgId(), role: m.role, text: m.text }))]);
      }
    }).catch(() => {});
  }, [sessionId, open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { inputRef.current?.focus(); }, 100);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const send = async (raw) => {
    const value = (raw ?? input).trim();
    if (!value) return;
    setInput("");
    setMessages((m) => [...m, { id: mkMsgId(), role: "user", text: value }]);
    setSending(true);
    try {
      const { data } = await api.post("/agent/chat", { message: value, session_id: sessionId });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try { localStorage.setItem(STORAGE_KEY, data.session_id); } catch (err) { /* privacy mode — non-fatal */ }
      }
      setMessages((m) => [...m, { id: mkMsgId(), role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { id: mkMsgId(), role: "assistant", text: `Nova hit a snag: ${e?.response?.data?.detail || "please try again."}` }]);
      toast.error("Nova couldn't respond");
    } finally {
      setSending(false);
    }
  };

  const quickCmd = (cmd) => {
    if (cmd === "/help") { send("/help"); return; }
    setInput(cmd);
    setTimeout(() => inputRef.current?.focus(), 60);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="nova-chip"
          aria-label="Open Nova AI assistant"
          className="fixed bottom-5 left-5 z-40 px-4 py-2.5 rounded-full bg-[#0A0A0A]/90 backdrop-blur-md border border-[#00E5FF]/40 hover:border-[#00E5FF] hover:bg-[#00E5FF]/10 text-[#00E5FF] shadow-[0_10px_30px_-8px_rgba(0,229,255,0.5)] transition-all flex items-center gap-2 group"
        >
          <Sparkle className="w-4 h-4 group-hover:rotate-12 transition-transform" />
          <span className="font-mono-hy text-[10px] tracking-widest">ASK NOVA</span>
        </button>
      )}
      {open && (
        <div
          data-testid="nova-panel"
          className="fixed bottom-5 left-5 z-40 w-[380px] max-w-[calc(100vw-24px)] h-[540px] max-h-[calc(100vh-40px)] rounded-2xl bg-[#120E15] border border-[#00E5FF]/40 shadow-[0_20px_60px_-10px_rgba(0,0,0,0.75)] overflow-hidden flex flex-col"
        >
          <div className="px-4 py-3 border-b border-[#00E5FF]/20 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#F2C94C] via-[#E27D60] to-[#00E5FF] flex items-center justify-center">
              <Sparkle className="w-4 h-4 text-[#0A0A0A]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm text-white leading-none">Nova</div>
              <div className="font-mono-hy text-[9px] text-[#00E5FF] leading-tight">GENEO · AI COPILOT · CLAUDE 4.6</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="nova-close"
              aria-label="Close Nova"
              className="w-7 h-7 rounded-full text-white hover:text-[#E27D60] hover:bg-[#E27D60]/10 flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="nova-thread">
            {messages.map((m, i) => (
              <div
                key={m.id}
                data-testid={`nova-msg-${i}`}
                className={`max-w-[85%] rounded-2xl px-3 py-2 font-body text-sm whitespace-pre-wrap ${
                  m.role === "user"
                    ? "ml-auto bg-[#F2C94C]/15 border border-[#F2C94C]/30 text-white"
                    : "mr-auto bg-[#00E5FF]/10 border border-[#00E5FF]/25 text-white"
                }`}
              >
                {m.text}
              </div>
            ))}
            {sending && (
              <div className="mr-auto bg-[#00E5FF]/10 border border-[#00E5FF]/25 rounded-2xl px-3 py-2 flex items-center gap-2" data-testid="nova-typing">
                <Loader2 className="w-3.5 h-3.5 text-[#00E5FF] animate-spin" />
                <span className="font-mono-hy text-[10px] text-[#00E5FF]">Nova is thinking…</span>
              </div>
            )}
          </div>

          <div className="px-3 py-2 border-t border-[#00E5FF]/15 flex items-center gap-1.5 flex-wrap">
            {QUICK_CMDS.map((c) => {
              const Icon = c.icon;
              return (
                <button
                  key={c.cmd}
                  onClick={() => quickCmd(c.cmd)}
                  data-testid={`nova-quick-${c.cmd.trim().replace("/", "")}`}
                  className="font-mono-hy text-[9px] px-2 py-1 rounded-full border border-[#00E5FF]/25 text-[#00E5FF] hover:bg-[#00E5FF]/10 flex items-center gap-1"
                >
                  <Icon className="w-3 h-3" /> {c.label}
                </button>
              );
            })}
          </div>

          <div className="p-3 border-t border-[#00E5FF]/15">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value.slice(0, MAX_LEN))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="Ask Nova…"
                rows={2}
                data-testid="nova-input"
                className="flex-1 min-h-[44px] max-h-[120px] px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#00E5FF]/20 focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/40 focus:outline-none font-body text-sm text-white placeholder:text-white resize-none"
              />
              <button
                onClick={() => send()}
                disabled={sending || !input.trim()}
                data-testid="nova-send"
                className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F2C94C] to-[#00E5FF] text-[#0A0A0A] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
